import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import PropTypes from 'prop-types';
import { effectiveBranchesOf } from '../data/store';
import { CHARS_PER_LINE, DEFAULT_TEMPLATE_ID, findPaperSize, resolveTemplateSettings } from '../data/billTemplates';
import { composeReceipt } from '../utils/composeReceipt';
import { ReceiptBlocks } from './ReceiptBlocks';
import { loadBillTemplates } from '../data/billTemplateStore';

/**
 * The on-screen bill, and what the browser prints.
 *
 * Two things were wrong before. It ignored the branch's design entirely — a
 * fixed layout with the word "Receipt", no GSTIN and a hard-coded thank you, so
 * a branch set to GST Tax Invoice printed a bill with no GSTIN on it. And it
 * was a third, hand-written copy of the receipt layout, which drifted from the
 * other two.
 *
 * It now renders the blocks from `composeReceipt`, exactly as the thermal
 * printers do. The bill on screen is the bill on paper, character for
 * character — including the absence of a rupee symbol, which ESC/POS code pages
 * cannot print reliably and so has never been on the paper.
 */
export const PrintBillModal = ({ bill, org, settings, title, onClose }) => {
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    loadBillTemplates()
      .then((list) => { if (!cancelled) setTemplates(list); });
    return () => { cancelled = true; };
  }, []);

  // On a shop running central inventory the bill's location is a warehouse, not
  // a branch, so there is no per-branch design to find. Falling back to the main
  // branch's keeps those bills on the shop's chosen design instead of silently
  // reverting to the standard preset; an unknown design falls back further, so a
  // deleted one can never leave the till with nothing to show.
  const cfg = useMemo(() => {
    // A design being previewed passes its own settings; a real bill has none and
    // uses whatever its branch prints with.
    if (settings) return settings;
    const list = effectiveBranchesOf(org.id);
    const branch = list.find((b) => b.id === bill.locationId)
      || list.find((b) => b.isMain)
      || list[0];
    return resolveTemplateSettings(branch?.billTemplateId || DEFAULT_TEMPLATE_ID, templates);
  }, [org.id, bill.locationId, templates, settings]);

  const { blocks, width } = useMemo(() => composeReceipt(org, bill, cfg), [org, bill, cfg]);

  // Any width the designs offer; an unknown one falls back to the standard roll
  // rather than printing at a size no printer has.
  const paper = CHARS_PER_LINE[cfg.paperWidth] ? cfg.paperWidth : 80;
  // Sized in millimetres, not pixels, so the card on screen is the width of the
  // roll it will print on — a 58mm design no longer previews at 80mm width and
  // then comes out of the printer wrapped. 3mm of padding matches the margin a
  // thermal head physically cannot print into.
  const sheet = { width: `${paper}mm`, padding: '3mm' };
  // A roll prints as long as the bill needs, so its page height is `auto`. A
  // sheet has an end, and asking for `210mm auto` on A4 gives a 210mm-wide
  // ribbon rather than a page — so a sheet states both dimensions.
  const spec = findPaperSize(paper);
  const pageSize = spec?.heightMm ? `${paper}mm ${spec.heightMm}mm` : `${paper}mm auto`;

  return (
  <div className="receipt-overlay fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4 print:bg-white print:p-0">
    <div
      className="receipt-card bg-white relative flex flex-col max-h-[90vh] rounded-none shadow-2xl overflow-hidden print:w-full print:max-h-none print:shadow-none text-black text-[10.5px] leading-tight"
      style={{ width: sheet.width }}
    >
      <style>{`
        @media print {
          @page { margin: 0; size: ${pageSize}; }
          body { margin: 0; padding: 0; background: white; }
          body * { visibility: hidden; }
          .print-receipt-modal, .print-receipt-modal * { visibility: visible; }

          /* The receipt sits in a scrolling box inside a height-capped card.
             On screen that is what makes a long bill scrollable; on paper it
             cut the bill off at whatever happened to be on screen — the
             totals and the footer simply never printed.

             The card gives up its height cap, its overflow and — the part that
             actually clipped — its positioning: an absolutely positioned box is
             clipped by an ancestor's overflow only when that ancestor is its
             containing block, and the card was. The overlay stays fixed, so it
             adds no height and the app behind it cannot push the receipt onto
             a later page. */
          .receipt-overlay {
            padding: 0 !important;
            background: #fff !important;
            display: block !important;
          }
          .receipt-card {
            position: static !important;
            display: block !important;
            width: 100% !important;
            max-height: none !important;
            overflow: visible !important;
            box-shadow: none !important;
          }
          .print-receipt-modal {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            flex: none !important;
          }
        }
      `}</style>

      {/* Only a design preview gets a header. A bill opened from Sales needs
          none — you already know which one you clicked — but a design is one of
          eight, and an unlabelled receipt does not say which. Hidden in print:
          it is a screen affordance, not part of the paper. */}
      {title && (
        <div className="shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 bg-white print:hidden">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 truncate">
            {title}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Close preview"
          >
            <X size={15} />
          </button>
        </div>
      )}

      <div className="print-receipt-modal flex-1 overflow-y-auto custom-scrollbar" style={{ padding: sheet.padding }}>
        <ReceiptBlocks blocks={blocks} width={width} />
      </div>

      {/* Modal Actions (Hidden when actually printing) */}
      <div className="shrink-0 border-t border-gray-200 p-4 bg-white flex items-center justify-center print:hidden rounded-none shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-10 relative">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-md text-xs font-bold text-white shadow-sm transition-all uppercase tracking-wider"
          style={{ background: '#ef4444', width: '100%', maxWidth: '140px' }}
        >
          Close Bill
        </button>
      </div>
    </div>
  </div>
  );
};

PrintBillModal.propTypes = {
  bill: PropTypes.object.isRequired,
  org: PropTypes.object.isRequired,
  settings: PropTypes.object,
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
